module.exports = { record, safeLocation, getSnapshot, clear, getAppState, getPushSummary, isEnabled, setEnabled };

const Storage = require('./storage');

const internals = { maxEvents: 100, session: 0 };
internals.debugKey = 'nearpeer:app:debug';

function isEnabled() {

    return Storage.get(internals.debugKey)?.appDiagnostics === true;
}

internals.erase = (state) => {

    Object.keys(state).forEach((key) => { delete state[key]; });
    Object.assign(state, { enabled: false, events: [], native: {} });
};

function setEnabled(value) {

    const enabled = value === true;
    const state = internals.state();
    const previous = typeof state.enabled === 'boolean' ? state.enabled : isEnabled();
    if (isEnabled() !== enabled) {
        Storage.set(internals.debugKey, { ...Storage.get(internals.debugKey), appDiagnostics: enabled });
    }

    if (previous !== enabled) { ++internals.session; }
    state.enabled = enabled;
    if (!enabled) {
        internals.erase(state);
        if (typeof window !== 'undefined' && window.__nearpeerCordovaBootstrapDebug) {
            window.__nearpeerCordovaBootstrapDebug.events = [];
        }
    }

    if (typeof window !== 'undefined') {
        if (previous !== enabled || internals.nativeBridge !== window.NearpeerAppLinks) {
            internals.nativeBridge = window.NearpeerAppLinks;
            try { window.NearpeerAppLinks?.setDiagnosticsEnabled?.(enabled, () => {}, () => {}); }
            catch (_) {}
        }

        if (previous !== enabled) {
            window.dispatchEvent(new Event('nearpeer:app-diagnostics-enabled'));
        }
    }
}

internals.state = () => {

    if (typeof window === 'undefined') {
        return { events: [], native: {} };
    }

    window.__nearpeerAppDiagnostics = window.__nearpeerAppDiagnostics || { events: [], native: {} };
    return window.__nearpeerAppDiagnostics;
};

function record(event, details = {}) {

    if (!isEnabled()) {
        internals.erase(internals.state());
        return;
    }

    const state = internals.state();
    state.events = (state.events || []).concat({ at: new Date().toISOString(), source: 'frontend', event, details }).slice(-internals.maxEvents);

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('nearpeer:app-diagnostics'));
    }
}

function safeLocation(value) {

    try {
        const input = typeof value === 'string' ? value : value?.href || `${value?.pathname || '/'}${value?.search || ''}`;
        const url = new URL(input, 'https://nearpeer.net');
        let sensitivePath = false;
        const pathname = url.pathname.split('/').map((part) => {

            let decoded;
            try { decoded = decodeURIComponent(part); }
            catch (_) { return '[invalid]'; }

            const redact = sensitivePath || decoded.includes('@') || decoded.length > 80;
            sensitivePath = sensitivePath || /^(token|code|email|password|secret|verification|debug)$/i.test(decoded);
            return redact ? '[redacted]' : part;
        }).join('/');

        return { hostname: url.hostname, pathname, queryKeys: [...url.searchParams.keys()] };
    }
    catch (_) {
        return { invalid: true };
    }
}

function getSnapshot() {

    const state = internals.state();
    if (!isEnabled()) {
        internals.erase(state);
    }
    return { ...state, recordingSession: internals.session, events: (state.events || []).slice() };
}

function clear() {

    internals.state().events = [];
    record('trace.cleared');
}

function getPushSummary(trace, receiver) {

    const events = trace.events || [];
    let start = -1;
    events.forEach(({ event }, index) => {

        if (event === 'push.native-received') { start = index; }
    });
    const received = start !== -1 || events.some(({ event }) => event === 'push.received');
    const attempt = events.slice(Math.max(start, 0));
    const payload = attempt.filter(({ event }) => event === 'push.received').pop();
    const attemptId = payload?.details?.pushAttemptId;
    const destination = attempt.find(({ event, details }) => event === 'push.destination' && details?.pushAttemptId === attemptId);
    const stages = attempt.filter(({ event, details }) => attemptId !== undefined
        ? details?.pushAttemptId === attemptId
        : /^(push\.(native-received|queued|failed|ignored))$/.test(event));
    const last = received ? stages[stages.length - 1] : undefined;
    const nativeDelivery = trace.native?.firebaseMessagingImmediatePayloadDelivery;

    return {
        status: received ? 'Push callback received.' : 'No push callback received in this trace.',
        receiver,
        nativeDelivery: nativeDelivery === false
            ? 'Immediate delivery disabled; Firebase can hold push callbacks in a scene-based iOS app. Rebuild the native app with the delivery fix.'
            : nativeDelivery === true ? 'Immediate delivery enabled.' : 'Not reported by this native build.',
        payloadDestination: payload?.details?.payloadDestination,
        requestedDestination: destination?.details?.destination,
        requestedSchoolId: destination?.details?.requestedSchoolId,
        destinationSource: destination?.details?.destinationSource,
        lastStage: last?.event,
        lastStageDetails: last?.details
    };
}

function getAppState({ selectors, state, location }) {

    const read = (name) => {

        try { return selectors?.[name]?.(state); }
        catch (_) { return 'unavailable'; }
    };

    return {
        authStatus: read('getAuthStatus'), authenticated: read('getIsAuthenticated'),
        verified: read('getVerifiedStatus'), bootstrapLoading: read('getMainAppLoading'),
        inBackground: read('getIsAppInBackground'), currentSchoolId: read('getCurrentSchoolId'),
        hasCurrentUser: read('hasCurrentUser'), hasCurrentSchool: read('hasCurrentSchool'),
        route: safeLocation(location)
    };
}
